import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ArrowLeft, ShoppingBag, X, CheckCircle, Trash2, ShoppingCart,Heart,AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../Profile/Header';
import CartItem from './CartItem';
import BillingSection from './BillingSection';
import CustomizationModal from '../Wishlist/CustomizationModal';
import TopPicks from '../Profile/TopPicks';
import { useCart } from '../context/CartContext';
import { useOrders } from '../context/OrdersContext';
import { PromoCodeCheckProvider } from '../context/PromoCodeCheckContext';
import { usePaymentGateway } from '../context/PaymentGatewayContext';
import toast from 'react-hot-toast';

// Enhanced Order Success Modal Component with immediate display and background processing
const OrderSuccessModal = ({ isOpen, orderDetails, onClose, onViewOrders, onContinueShopping, isProcessingOrder = false }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-teal-50 px-6 py-6 text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-teal-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {isProcessingOrder ? 'Payment Successful!' : 'Order Placed Successfully!'}
          </h3>
          <p className="text-sm text-gray-600">
            {isProcessingOrder ? 'Processing your order...' : 'Thank you for your purchase'}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {orderDetails && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Order ID:</span>
                  <span className="font-semibold text-gray-900">
                    {isProcessingOrder ? 'Generating...' : (orderDetails.orderId || 'Generating...')}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Amount:</span>
                  <span className="font-medium text-gray-900">₹{orderDetails.total || '0.00'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Items:</span>
                  <span className="font-medium text-gray-900">{orderDetails.itemCount || 0} item(s)</span>
                </div>
                {isProcessingOrder && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-medium text-teal-600 flex items-center gap-1">
                      <div className="w-2 h-2 bg-teal-600 rounded-full animate-pulse"></div>
                      Processing...
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {isProcessingOrder && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Your payment was successful. We're now processing your order and you'll receive a confirmation email shortly.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex flex-col gap-3">
            <button
              onClick={onViewOrders}
              disabled={isProcessingOrder}
              className={`w-full px-4 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center gap-2 ${
                isProcessingOrder
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-teal-600 text-white hover:bg-teal-700 focus:ring-teal-500'
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
              {isProcessingOrder ? 'Processing Order...' : 'View My Orders'}
            </button>
            <button
              onClick={onContinueShopping}
              className="w-full px-4 py-2 text-teal-700 bg-white border border-teal-300 rounded-lg hover:bg-teal-50 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 flex items-center justify-center gap-2"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Cart = () => {
  const navigate = useNavigate();

  const {
    cart: cartItems,
    removeFromCart,
    updateQuantity,
    clearCart,
    removeProductById,
    validateAndCleanCart
  } = useCart();
  const { placeOrder, refreshOrders } = useOrders();
  const { createPaymentOrder, forceCleanup, isPaymentActive } = usePaymentGateway();

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user.id;

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [customizationModal, setCustomizationModal] = useState({
    isOpen: false,
    product: null
  });

  const [orderSuccessModal, setOrderSuccessModal] = useState({
    isOpen: false,
    orderDetails: null,
    isProcessingOrder: false
  });

  // Loading state for checkout
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [pendingCheckoutData, setPendingCheckoutData] = useState(null);

  // Add state to track if order placement is in progress
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  // Add state to track cart validation
  const [isValidatingCart, setIsValidatingCart] = useState(false);

  // Add refs to prevent duplicate order placement
  const orderPlacementInProgress = useRef(false);
  const lastOrderId = useRef(null);
  const paymentSuccessHandled = useRef(false);
  const paymentAttemptCount = useRef(0); // Track payment attempts

  // Validate cart on component mount and when user changes
  useEffect(() => {
    const validateCartOnMount = async () => {
      if (cartItems.length > 0 && !isValidatingCart) {
        setIsValidatingCart(true);
        try {
          console.log('Validating cart on component mount...');
          const result = await validateAndCleanCart();

          if (result.invalidCartItems && result.invalidCartItems.length > 0) {
            toast.error(`${result.invalidCartItems.length} item(s) were removed from your cart as they are no longer available.`, {
              duration: 5000,
            });
          }

          if (result.validCartItems && result.validCartItems.length > 0) {
            console.log('Cart updated with fresh product data');
          }
        } catch (error) {
          console.error('Error validating cart on mount:', error);
        } finally {
          setIsValidatingCart(false);
        }
      }
    };

    // Validate cart after a short delay to ensure component is fully mounted
    const timeoutId = setTimeout(validateCartOnMount, 500);

    return () => clearTimeout(timeoutId);
  }, [userId]); // Re-run when user changes

  // Cleanup payment context on component unmount
  useEffect(() => {
    return () => {
      if (forceCleanup) {
        forceCleanup();
      }
      // Reset refs on unmount
      orderPlacementInProgress.current = false;
      lastOrderId.current = null;
      paymentSuccessHandled.current = false;
      paymentAttemptCount.current = 0;
    };
  }, [forceCleanup]);

  // Scroll to top when OrderSuccessModal opens
  useEffect(() => {
    if (orderSuccessModal.isOpen) {
      window.scrollTo(0, 0);
    }
  }, [orderSuccessModal.isOpen]);

  // Filter cart items based on search query
  const filteredCartItems = useMemo(() => {
    if (!searchQuery.trim()) return cartItems;

    return cartItems.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.categoryId?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [cartItems, searchQuery]);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    const index = cartItems.findIndex(item => item.id === itemId);
    if (index > -1) updateQuantity(index, newQuantity);
  };

  const handleRemoveItem = (itemId) => {
    const index = cartItems.findIndex(item => item.id === itemId);
    if (index > -1) removeFromCart(index);
  };

  const handleMoveToWishlist = (itemId) => {
    console.log('Moving item to wishlist:', itemId);
    handleRemoveItem(itemId);
    toast.success('Item moved to wishlist!', {
      icon: <Heart size={16} className="text-red-600" fill="currentColor" />,
    });
  };

  const handleCustomizeClick = (product) => {
    setCustomizationModal({
      isOpen: true,
      product: product
    });
  };

  const handleCloseModal = () => {
    setCustomizationModal({
      isOpen: false,
      product: null
    });
  };

  // Optimized handle order success modal - navigate immediately for better UX
  const handleViewOrders = async () => {
    // Close modal immediately for instant feedback
    setOrderSuccessModal({ isOpen: false, orderDetails: null, isProcessingOrder: false });

    // Navigate immediately to provide instant user feedback
    navigate('/orders');

    // Refresh orders in the background after navigation
    // This ensures the user sees immediate response while data loads in background
    try {
      // Use setTimeout to ensure navigation happens first
      setTimeout(async () => {
        try {
          await refreshOrders();
          console.log('Orders refreshed successfully in background');
        } catch (error) {
          console.error('Failed to refresh orders in background:', error);
          // Don't show error toast as user has already navigated
          // The orders page will handle loading states and errors
        }
      }, 100);
    } catch (error) {
      console.error('Failed to initiate background refresh:', error);
    }
  };

  const handleContinueShoppingFromSuccess = () => {
    setOrderSuccessModal({ isOpen: false, orderDetails: null, isProcessingOrder: false });
    navigate('/products');
  };

  // Helper function to get item price (preserving user's improved logic)
  const getItemPrice = (item) => {
    // Use offerPrice if available and valid, otherwise fall back to price
    if (item.offerPrice && item.offerPrice > 0) {
      return Number(item.offerPrice);
    }

    // Use your_price if available, otherwise fall back to price
    if (item.your_price) {
      return Number(item.your_price);
    }

    // Fallback to item.price
    if (typeof item.price === "string") {
      return parseFloat(item.price.replace(/[₹$]/g, ''));
    }

    return Number(item.price) || 0;
  };

  // Enhanced function to place order after successful payment with immediate modal display
  const placeOrderAfterPayment = async (formData, itemsToOrder, paymentResult) => {
    console.log('=== STARTING ORDER PLACEMENT AFTER SUCCESSFUL PAYMENT ===');
    console.log('Payment attempt count:', paymentAttemptCount.current);
    console.log('Payment result:', paymentResult);

    // Prevent duplicate order placement
    if (orderPlacementInProgress.current) {
      console.log('Order placement already in progress, skipping duplicate');
      return;
    }

    // Check if this payment was already handled
    if (paymentSuccessHandled.current) {
      console.log('Payment success already handled, skipping duplicate');
      return;
    }

    // Mark as handled immediately
    orderPlacementInProgress.current = true;
    paymentSuccessHandled.current = true;

    try {
      const promoCode = formData.promo_code || '';
      const promoDiscount = formData.promo_discount || 0;

      // Calculate base total for all items using correct price
      const baseItemsTotal = itemsToOrder.reduce((total, item) => {
        const price = getItemPrice(item);
        return total + price * item.quantity;
      }, 0);

      // Use calculated totals from BillingSection if available, otherwise calculate manually
      let finalTotal;
      let subtotalAfterDiscount;

      if (formData.calculatedTotals) {
        // Use the pre-calculated totals from BillingSection (includes proper discount application)
        finalTotal = formData.calculatedTotals.total;
        subtotalAfterDiscount = formData.calculatedTotals.discountedSubtotal;
        console.log('Using pre-calculated totals from BillingSection:', formData.calculatedTotals);
      } else {
        // Fallback calculation (for backward compatibility)
        const shippingCharges = formData.shipping_charges || 0;
        const tax = formData.tax || 0;
        subtotalAfterDiscount = Math.max(0, baseItemsTotal + shippingCharges - promoDiscount);
        finalTotal = subtotalAfterDiscount + tax;
        console.log('Using fallback total calculation:', {
          baseItemsTotal,
          shippingCharges,
          tax,
          promoDiscount,
          subtotalAfterDiscount,
          finalTotal
        });
      }

      // Create unique order identifier to prevent duplicates
      const orderIdentifier = `${userId}_${paymentResult?.paymentResponse?.razorpay_payment_id}_${Date.now()}`;

      // Calculate order details for immediate modal display
      const immediateOrderDetails = {
        orderId: 'Generating...', // Will be updated once order is placed
        total: finalTotal.toFixed(2),
        itemCount: itemsToOrder.length
      };

      console.log('=== SHOWING SUCCESS MODAL IMMEDIATELY ===');
      // Show success modal IMMEDIATELY with processing state
      setOrderSuccessModal({
        isOpen: true,
        orderDetails: immediateOrderDetails,
        isProcessingOrder: true
      });

      // Clear cart immediately after payment success (before API call)
      clearCart();

      // Reset checkout states immediately
      setIsCheckingOut(false);
      setIsPlacingOrder(false);
      setPendingCheckoutData(null);

      console.log('Success modal displayed immediately, now placing order in background...');

      // Now place the order in the background
      const orderData = {
        user_id: userId,
        address_id: formData.address_id,
        shipping_method_id: formData.shipping_method_id || "1",
        products: itemsToOrder.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          price: getItemPrice(item)
        })),
        shipping_charges: formData.shipping_charges || 0,
        tax: formData.tax || 0,
        promo_code: promoCode,
        promo_discount: promoDiscount,
        // Send the correctly calculated total (after promo discount)
        total: finalTotal,
        subtotal: subtotalAfterDiscount,
        // Payment gateway integration - always online now
        payment_method: "Online",
        payment_status: "Paid",
        transaction_id: paymentResult?.paymentResponse?.razorpay_payment_id,
        payment_gateway: "Razorpay",
        payment_response: paymentResult?.paymentResponse || {},
        // Include billing information from formData
        billing_first_name: formData.billing_first_name || '',
        billing_last_name: formData.billing_last_name || '',
        billing_email: formData.billing_email || '',
        billing_phone: formData.billing_phone || '',
        billing_address: formData.billing_address || '',
        billing_city: formData.billing_city || '',
        billing_state: formData.billing_state || '',
        billing_pincode: formData.billing_pincode || '',
        // Add order identifier for duplicate prevention
        order_identifier: orderIdentifier
      };

      console.log('=== PLACING ORDER IN BACKGROUND ===');
      console.log('Final order data being sent:', orderData);

      // Place order in background
      const response = await placeOrder(orderData);
      console.log('=== ORDER PLACED SUCCESSFULLY ===');
      console.log('Order response:', response);

      // Store the order ID to prevent duplicates
      lastOrderId.current = response.data?.order_id || response.order_id;

      // Update the modal with the actual order ID
      const finalOrderDetails = {
        orderId: response.data?.order_id || response.order_id || 'Generated',
        total: finalTotal.toFixed(2),
        itemCount: itemsToOrder.length
      };

      // Update modal to show completion
      setOrderSuccessModal({
        isOpen: true,
        orderDetails: finalOrderDetails,
        isProcessingOrder: false
      });

      console.log('=== ORDER PLACEMENT COMPLETED SUCCESSFULLY ===');

    } catch (error) {
      console.error('=== ORDER PLACEMENT ERROR AFTER PAYMENT ===');
      console.error('Error details:', error);
      const errorMessage = error.response?.data?.message || 'Something went wrong while placing your order after payment.';

      // Reset states on error
      orderPlacementInProgress.current = false;
      paymentSuccessHandled.current = false;

      // Update modal to show error state but keep it open since payment was successful
      setOrderSuccessModal({
        isOpen: true,
        orderDetails: {
          orderId: 'Error - Contact Support',
          total: formData.calculatedTotals?.total?.toFixed(2) || '0.00',
          itemCount: itemsToOrder.length
        },
        isProcessingOrder: false
      });

      // Show error but don't reset checkout state completely since payment was successful
      toast.error(`Payment successful but order placement failed: ${errorMessage}. Please contact support with your payment details.`, {
        duration: 8000,
      });
    }
  };

  // Process checkout with specific items (integrated with payment gateway)
  const processCheckoutWithItems = async (formData, itemsToOrder) => {
    if (itemsToOrder.length === 0) {
      toast.error('No items to order!');
      setPendingCheckoutData(null);
      setIsCheckingOut(false); // Reset checkout state
      return;
    }

    // Check if payment is already active
    if (isPaymentActive && isPaymentActive()) {
      toast.error('Payment is already in progress. Please wait.', {
        icon: <AlertTriangle size={16} className="text-orange-600" />,
      });
      return;
    }

    // Reset all states at the beginning of new checkout attempt
    setIsCheckingOut(true);
    setIsPlacingOrder(false);
    orderPlacementInProgress.current = false;
    paymentSuccessHandled.current = false;
    paymentAttemptCount.current += 1;

    console.log(`=== STARTING CHECKOUT ATTEMPT #${paymentAttemptCount.current} ===`);

    try {
      console.log('Checkout with items:', itemsToOrder);
      console.log('Checkout form data received:', formData);

      // Ensure promo_code is properly extracted and included
      const promoCode = formData.promo_code || '';
      const promoDiscount = formData.promo_discount || 0;

      // Calculate base total for all items using correct price
      const baseItemsTotal = itemsToOrder.reduce((total, item) => {
        const price = getItemPrice(item);
        return total + price * item.quantity;
      }, 0);

      // Use calculated totals from BillingSection if available, otherwise calculate manually
      let finalTotal;

      if (formData.calculatedTotals) {
        // Use the pre-calculated totals from BillingSection (includes proper discount application)
        finalTotal = formData.calculatedTotals.total;
        console.log('Using pre-calculated totals from BillingSection:', formData.calculatedTotals);
      } else {
        // Fallback calculation (for backward compatibility)
        const shippingCharges = formData.shipping_charges || 0;
        const tax = formData.tax || 0;
        const subtotalAfterDiscount = Math.max(0, baseItemsTotal + shippingCharges - promoDiscount);
        finalTotal = subtotalAfterDiscount + tax;
        console.log('Using fallback total calculation:', {
          baseItemsTotal,
          shippingCharges,
          tax,
          promoDiscount,
          subtotalAfterDiscount,
          finalTotal
        });
      }

      // Ensure minimum amount for Razorpay
      finalTotal = Math.max(finalTotal, 1);

      // Always online payment now (no COD)
      console.log('Processing online payment for amount:', finalTotal);

      // Enhanced payment options with improved callbacks
      const paymentOptions = {
        amount: finalTotal,
        user: user,
        onSuccess: async (response) => {
          console.log('=== PAYMENT SUCCESS CALLBACK TRIGGERED ===');
          console.log('Payment response:', response);
          console.log('Payment attempt count:', paymentAttemptCount.current);

          // Call the formData callback if provided
          if (formData.onPaymentSuccess) {
            try {
              formData.onPaymentSuccess(response);
            } catch (error) {
              console.error('Error in formData.onPaymentSuccess:', error);
            }
          }

          // Create payment result object
          const paymentResult = {
            success: true,
            paymentResponse: response,
            orderId: response.razorpay_order_id
          };

          // Place order after successful payment with immediate modal display
          await placeOrderAfterPayment(formData, itemsToOrder, paymentResult);
        },
        onFailure: (error) => {
          console.log('=== PAYMENT FAILURE CALLBACK TRIGGERED ===');
          console.log('Payment failure error:', error);
          console.log('Payment attempt count:', paymentAttemptCount.current);

          // Call the formData callback if provided
          if (formData.onPaymentFailure) {
            try {
              formData.onPaymentFailure(error);
            } catch (error) {
              console.error('Error in formData.onPaymentFailure:', error);
            }
          }

          // Reset states on payment failure - CRITICAL: Reset all flags to allow retry
          setIsCheckingOut(false);
          setIsPlacingOrder(false);
          setPendingCheckoutData(null);
          orderPlacementInProgress.current = false;
          paymentSuccessHandled.current = false; // This is crucial for retry scenarios

          console.log('=== PAYMENT FAILURE - STATES RESET FOR RETRY ===');

          toast.error("Payment failed. Please try again.", {
            duration: 5000,
            icon: <X size={16} className="text-red-600" />,
          });
        },
        onModalClose: () => {
          console.log('=== PAYMENT MODAL CLOSED BY USER ===');
          console.log('Payment attempt count:', paymentAttemptCount.current);

          // Call the formData callback if provided
          if (formData.onModalClose) {
            try {
              formData.onModalClose();
            } catch (error) {
              console.error('Error in formData.onModalClose:', error);
            }
          }

          // Reset states to allow retry - CRITICAL: Ensure all flags are reset
          setIsCheckingOut(false);
          setIsPlacingOrder(false);
          setPendingCheckoutData(null);
          orderPlacementInProgress.current = false;
          paymentSuccessHandled.current = false; // Reset to allow fresh payment attempt

          console.log('=== MODAL CLOSED - STATES RESET FOR RETRY ===');
        }
      };

      // Initiate payment with better error handling
      try {
        console.log('=== INITIATING PAYMENT ===');
        const paymentResult = await createPaymentOrder(paymentOptions.amount, paymentOptions.user, {
          onSuccess: paymentOptions.onSuccess,
          onFailure: paymentOptions.onFailure,
          onModalClose: paymentOptions.onModalClose
        });

        // Note: The success flow is now handled in the onSuccess callback above
        // This code will only run if the payment gateway returns success synchronously (which it shouldn't)
        if (paymentResult && paymentResult.success) {
          console.log('Synchronous payment success (unexpected):', paymentResult);
          await placeOrderAfterPayment(formData, itemsToOrder, paymentResult);
        }
      } catch (paymentError) {
        console.error('=== PAYMENT INITIATION FAILED ===');
        console.error('Payment error:', paymentError);

        // Reset states on payment initiation error - CRITICAL: Reset all flags
        setIsCheckingOut(false);
        setIsPlacingOrder(false);
        setPendingCheckoutData(null);
        orderPlacementInProgress.current = false;
        paymentSuccessHandled.current = false; // Ensure this is reset for retry

        if (paymentError.cancelled) {
          // Payment was cancelled by user - don't show additional error
          console.log('Payment cancelled by user');
        } else if (paymentError.message === "Payment already in progress") {
          // Already handled by PaymentGatewayContext
          console.log('Duplicate payment attempt blocked');
        } else {
          // Actual error occurred
          const errorMessage = paymentError.message || 'Failed to initiate payment.';
          toast.error(errorMessage, {
            duration: 5000,
          });
        }

        throw paymentError;
      }

    } catch (error) {
      console.error('=== CHECKOUT PROCESSING ERROR ===');
      console.error('Error details:', error);

      // Reset states on error - CRITICAL: Ensure all flags are reset
      setIsCheckingOut(false);
      setIsPlacingOrder(false);
      setPendingCheckoutData(null);
      orderPlacementInProgress.current = false;
      paymentSuccessHandled.current = false; // Reset to allow retry after error

      // Error handling is now done in the payment initiation try-catch above
    }
  };

  // Process checkout after validation (uses current cart items)
  const processCheckout = async (formData) => {
    // Double check if cart is empty before proceeding
    if (cartItems.length === 0) {
      toast.error('Your cart is empty! Please add items before checkout.');
      setPendingCheckoutData(null);
      return;
    }

    // Use the current cart items for checkout
    processCheckoutWithItems(formData, cartItems);
  };

  // Simplified checkout handler
  const handleCheckout = async (formData) => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty!');
      return;
    }

    console.log('Processing checkout with items:', cartItems.map(item => ({ id: item.id, name: item.name })));

    // Proceed directly to checkout
    processCheckout(formData);
  };

  const handleContinueShopping = () => {
    navigate('/products');
  };

  // Calculate cart total using correct price fields
  const cartTotal = cartItems.reduce((total, item) => {
    const price = getItemPrice(item);
    return total + price * item.quantity;
  }, 0);

  return (
    <PromoCodeCheckProvider>
      <div className="relative mx-auto my-0 bg-orange-50 bg-opacity-90 h-auto w-full">
        {/* Header */}
        <Header
          activeSection="cart"
          onSearch={handleSearch}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
        />

        {/* Main Content */}
        <main className="relative mx-auto w-[90%] lg:w-[80%] xl:w-[75%] 2xl:w-[70%] mt-6 mb-8 md:mt-1 px-4 md:px-0">
          {/* Cart Header */}
          <header className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-between items-start  w-full text-teal-800 mb-6 md:mb-8">
            <div className="flex items-center gap-4">
              <ShoppingCart className="w-8 h-8 text-teal-600" />
              <h1 className="text-2xl md:text-3xl lg:text-4xl tracking-wider font-bold text-teal-800">
                Shopping Cart
              </h1>
            </div>

            {cartItems.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-center gap-2 text-sm md:text-base text-gray-600">
                  <ShoppingBag className="w-5 h-5" />
                  <span>
                    {searchQuery ? `${filteredCartItems.length} of ${cartItems.length}` : cartItems.length} item{cartItems.length !== 1 ? 's' : ''}
                    {searchQuery && ` matching "${searchQuery}"`}
                  </span>
                </div>
              </div>
            )}
          </header>

          {/* Cart Content */}
          {cartItems.length === 0 ? (
            <section className="text-center py-12 md:py-16" aria-label="Empty cart message">
              <div className="max-w-md mx-auto px-4">
                <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <ShoppingBag className="w-10 h-10 md:w-12 md:h-12 text-gray-400" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">Your cart is empty</h2>
                <p className="text-gray-600 mb-6 text-sm md:text-base">
                  Add some items to your cart and they'll appear here.
                </p>
                <button
                  onClick={handleContinueShopping}
                  className="bg-teal-800 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm md:text-base"
                >
                  Start Shopping
                </button>
              </div>
            </section>
          ) : filteredCartItems.length === 0 && searchQuery ? (
            <section className="text-center py-12 md:py-16" aria-label="No search results">
              <div className="max-w-md mx-auto px-4">
                <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <ShoppingBag className="w-10 h-10 md:w-12 md:h-12 text-gray-400" />
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">No items found</h2>
                <p className="text-gray-600 mb-6 text-sm md:text-base">
                  No cart items match your search for "{searchQuery}".
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="bg-teal-800 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm md:text-base"
                >
                  Show All Items
                </button>
              </div>
            </section>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items - Left Side */}
              <section className="lg:col-span-2" aria-label="Cart items">
                <div className="space-y-6">
                  {/* Cart Items Section */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        Cart Items ({filteredCartItems.length})
                      </h3>
                      <span className="text-sm text-gray-600">Ready for checkout</span>
                    </div>

                    {filteredCartItems.map((item) => (
                      <CartItem
                        key={item.id}
                        item={item}
                        onQuantityChange={handleQuantityChange}
                        onRemove={handleRemoveItem}
                        onMoveToWishlist={handleMoveToWishlist}
                        onCustomizeClick={handleCustomizeClick}
                      />
                    ))}
                  </div>
                </div>

                {/* Cart Summary for Mobile */}
                <div className="lg:hidden mt-6 p-4 bg-white bg-opacity-60 rounded-xl border border-orange-200">
                  <div className="flex justify-between items-center text-lg font-semibold">
                    <span>Subtotal ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''}):</span>
                    <span className="text-teal-700">₹{cartTotal.toFixed(2)}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Shipping and taxes calculated at checkout
                  </p>
                </div>
              </section>

              {/* Billing Section - Right Side */}
              <section className="lg:col-span-1" aria-label="Checkout">
                {/* <div className="sticky top-6"> */}
                <div className="">
                  <BillingSection
                    cartTotal={cartTotal}
                    onCheckout={handleCheckout}
                    isLoading={isCheckingOut || isPlacingOrder}
                    onResetLoading={() => {
                      console.log('Cart: Manual reset loading triggered from BillingSection');
                      setIsCheckingOut(false);
                      setIsPlacingOrder(false);
                      setPendingCheckoutData(null);
                      orderPlacementInProgress.current = false;
                      paymentSuccessHandled.current = false;
                      paymentAttemptCount.current = 0;
                    }}
                  />
                </div>
              </section>
            </div>
          )}
        </main>

        {/* Modals */}
        <CustomizationModal
          isOpen={customizationModal.isOpen}
          onClose={handleCloseModal}
          product={customizationModal.product}
        />

        <OrderSuccessModal
          isOpen={orderSuccessModal.isOpen}
          orderDetails={orderSuccessModal.orderDetails}
          isProcessingOrder={orderSuccessModal.isProcessingOrder}
          onClose={() => setOrderSuccessModal({ isOpen: false, orderDetails: null, isProcessingOrder: false })}
          onViewOrders={handleViewOrders}
          onContinueShopping={handleContinueShoppingFromSuccess}
        />
      </div>
    </PromoCodeCheckProvider>
  );
};

export default Cart;
